import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import * as api from "../api";
import { useToast } from "../components/Toast";
import DownloadMenu from "../components/DownloadMenu";

/**
 * Reports — portfolio-wide aggregations.
 *
 *   • Key Projection — keys (rooms) by FY across the filtered project
 *     set, with cumulative + a Pipeline bucket for not-yet-slotted keys.
 *   • Project Finance — year-wise promoters contribution + bank loan
 *     totals (each project's MoF ratio splits its annual capex outflow),
 *     with cumulative columns.
 *
 *   Filters apply identically to every tab: Vertical / Company / Project /
 *   Year range. Picking a project narrows everything to that one project.
 */

const fmt = (n) => {
  if (n == null || isNaN(n) || Number(n) === 0) return "—";
  return Math.round(Number(n)).toLocaleString("en-IN");
};

const fyLabel = (y) => `FY ${String(y).slice(-2)}-${String(y + 1).slice(-2)}`;

/** Small i chip that shows the description on hover. Replaces the
 *  paragraph of small text below each tab heading. */
function InfoTip({ text }) {
  return (
    <span title={text}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 18, height: 18, borderRadius: "50%",
        border: "1px solid var(--border-input)",
        background: "rgba(124,143,245,0.08)", color: "var(--text-muted)",
        fontSize: 11, cursor: "help", fontWeight: 700,
        fontFamily: "'Georgia', 'Times New Roman', serif", fontStyle: "italic",
      }}>i</span>
  );
}


/* ─── MultiSelect with built-in search ───────────────────────────
   Replaces a single-select <select> with a dropdown that:
     • Shows checkboxes for each option (multi-select)
     • Has a search box that filters by label substring
     • Reports its value as an array of ids; emits the full set on change
     • Shows a chip summary in the trigger button
   No external dep — pure React + inline styles.
*/
function MultiSelect({ label, options, value, onChange, placeholder = "— Any —" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = new Set(value || []);
  const q = (search || "").trim().toLowerCase();
  const visible = q
    ? options.filter((o) => (o.label || "").toLowerCase().includes(q))
    : options;
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange([...next]);
  };
  const clearAll = () => onChange([]);

  const triggerLabel = value && value.length === 0
    ? placeholder
    : value.length === 1
      ? (options.find((o) => o.id === value[0])?.label || value[0])
      : `${value.length} selected`;

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block", minWidth: 180 }}>
      <button type="button"
        className="input select"
        style={{
          width: "100%", textAlign: "left",
          paddingRight: 28, cursor: "pointer",
          color: value.length ? "var(--text-primary)" : "var(--text-muted)",
        }}
        onClick={() => setOpen((p) => !p)}>
        {triggerLabel}
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0,
          minWidth: 240, zIndex: 50,
          background: "var(--bg-secondary, #15192a)",
          border: "1px solid var(--border)", borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
          padding: 8,
        }}>
          <div className="flex items-center gap-6 mb-6">
            <input type="text" autoFocus
              className="input input-sm" placeholder={`Search ${label || "options"}…`}
              value={search} onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, padding: "4px 8px" }} />
            {value.length > 0 && (
              <button type="button" className="btn btn-ghost btn-sm"
                onClick={clearAll} title="Clear selection">Clear</button>
            )}
          </div>
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {visible.length === 0 && (
              <p className="text-xxs text-dim" style={{ padding: "8px 6px" }}>
                No matches.
              </p>
            )}
            {visible.map((o) => (
              <label key={o.id} className="flex items-center gap-8"
                style={{
                  cursor: "pointer", padding: "5px 8px", borderRadius: 6,
                  background: selected.has(o.id) ? "rgba(124,143,245,0.10)" : "transparent",
                }}>
                <input type="checkbox" checked={selected.has(o.id)}
                  onChange={() => toggle(o.id)}
                  style={{ accentColor: "var(--accent)" }} />
                <span className="text-xs" style={{
                  flex: 1, overflow: "hidden",
                  textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{o.label}</span>
                {o.hint && <span className="text-xxs text-dim">{o.hint}</span>}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ReportPage() {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState("keys");
  const [layout, setLayout] = useState("stacked");  // "stacked" | "side-by-side"
  const [verticals, setVerticals] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [years, setYears] = useState([]);
  // Filters are arrays (multi-select). Serialized to comma-separated
  // strings on the way to the API.
  const [filters, setFilters] = useState({
    vertical_id: [], company_id: [], project_id: [],
  });
  const [keyData, setKeyData] = useState(null);
  const [finData, setFinData] = useState(null);
  const [cashData, setCashData] = useState(null);
  const [pnlData, setPnlData] = useState(null);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [loadingFin, setLoadingFin] = useState(false);
  const [loadingCash, setLoadingCash] = useState(false);
  const [loadingPnl, setLoadingPnl] = useState(false);

  // Load facets once on mount.
  useEffect(() => {
    Promise.all([
      api.getVerticals().catch(() => []),
      api.getCompanies().catch(() => []),
      api.getProjects({ light: true }).catch(() => []),
    ]).then(([v, c, p]) => {
      setVerticals(Array.isArray(v) ? v : []);
      setCompanies(Array.isArray(c) ? c : []);
      setProjects(Array.isArray(p) ? p : []);
      // FY range derived from project actual_years.
      const ys = new Set();
      (p || []).forEach((proj) => {
        const ay = proj.actual_year || new Date().getFullYear();
        for (let i = 0; i < 6; i += 1) ys.add(ay + i);
      });
      setYears([...ys].sort((a, b) => a - b));
    });
  }, []);

  // ── Hierarchical company expansion: picking a Main expands to all
  //    descendant companies (matches backend behaviour). Used to narrow
  //    the project list dropdown.
  const expandedCompanyIds = useMemo(() => {
    const explicit = new Set(filters.company_id || []);
    if (explicit.size === 0) return explicit;
    const kids = new Map();
    companies.forEach((c) => {
      if (c.parent_company_id) {
        if (!kids.has(c.parent_company_id)) kids.set(c.parent_company_id, []);
        kids.get(c.parent_company_id).push(c.id);
      }
    });
    const out = new Set(explicit);
    const stack = [...explicit];
    while (stack.length) {
      const cur = stack.pop();
      for (const k of kids.get(cur) || []) {
        if (!out.has(k)) { out.add(k); stack.push(k); }
      }
    }
    return out;
  }, [companies, filters.company_id]);

  // Filter dropdown options narrow when something upstream is picked:
  //   - Company list filters to those with at least one matching project
  //   - Project list filters to those under the selected vertical + company
  //     (vertical via OR, since multi-select)
  const filteredCompanies = useMemo(() => {
    if (filters.vertical_id.length === 0) return companies;
    const verticalSet = new Set(filters.vertical_id);
    const cidsWithMatch = new Set(
      projects.filter((p) => verticalSet.has(p.vertical_id)).map((p) => p.company_id),
    );
    return companies.filter((c) => cidsWithMatch.has(c.id));
  }, [companies, projects, filters.vertical_id]);

  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      if (filters.vertical_id.length && !filters.vertical_id.includes(p.vertical_id)) return false;
      if (expandedCompanyIds.size && !expandedCompanyIds.has(p.company_id)) return false;
      return true;
    });
  }, [projects, filters.vertical_id, expandedCompanyIds]);

  // Refetch report data whenever filters change. Array filters are
  // serialized as comma-separated strings — the backend accepts both
  // single-value and multi-value via _csv_param().
  const loadReports = useCallback(() => {
    setLoadingKeys(true);
    setLoadingFin(true);
    setLoadingCash(true);
    setLoadingPnl(true);
    const apiFilters = {
      vertical_id: (filters.vertical_id || []).join(","),
      company_id:  (filters.company_id  || []).join(","),
      project_id:  (filters.project_id  || []).join(","),
    };
    Promise.all([
      api.getKeyProjection(apiFilters).catch(() => null),
      api.getProjectFinance(apiFilters).catch(() => null),
      api.getCashflow(apiFilters).catch(() => null),
      api.getPnl(apiFilters).catch(() => null),
    ]).then(([k, f, c, p]) => {
      setKeyData(k);
      setFinData(f);
      setCashData(c);
      setPnlData(p);
    }).catch(() => toast("Failed to load report data", "error"))
    .finally(() => {
      setLoadingKeys(false); setLoadingFin(false);
      setLoadingCash(false); setLoadingPnl(false);
    });
  }, [filters, toast]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const setF = (k, v) => setFilters((p) => {
    // Reset narrower filters when a broader one changes.
    const next = { ...p, [k]: v };
    if (k === "vertical_id") { next.company_id = []; next.project_id = []; }
    if (k === "company_id")  { next.project_id = []; }
    return next;
  });
  const resetFilters = () => setFilters({
    vertical_id: [], company_id: [], project_id: [],
  });
  // React renders a literal `0` when a falsy number gates JSX (the classic
  // `{0 && <X />}` trap). Coerce to a boolean so the Reset button
  // conditional doesn't print a stray "0" when no filters are active.
  const hasFilter = !!(filters.vertical_id?.length || filters.company_id?.length
    || filters.project_id?.length);

  return (
    <div className="fade-in">
      {/* ── Filter strip — multi-select with search ──
          Hierarchical: picking a high-level company (e.g. ANHVL)
          automatically expands to every descendant via Company.parent.
          All filters compose with AND across types, OR within a type. */}
      <div className="card mb-12" style={{ padding: "8px 12px" }}>
        <div className="flex items-center" style={{ gap: 8, flexWrap: "wrap" }}>
          <span className="text-xxs text-muted fw-600" style={{ textTransform: "uppercase", letterSpacing: 0.8, marginRight: 4 }}>
            Filter:
          </span>
          <MultiSelect
            label="vertical"
            options={verticals.map((v) => ({ id: v.id, label: v.name }))}
            value={filters.vertical_id}
            onChange={(arr) => setF("vertical_id", arr)}
            placeholder="— Any vertical —" />
          <MultiSelect
            label="company"
            options={filteredCompanies.map((c) => ({
              id: c.id,
              label: `${c.name}${c.code ? ` (${c.code})` : ""}`,
              hint: c.parent_company_id ? "" : "Main",
            }))}
            value={filters.company_id}
            onChange={(arr) => setF("company_id", arr)}
            placeholder="— Any company —" />
          <MultiSelect
            label="project"
            options={filteredProjects.map((p) => ({ id: p.id, label: p.name }))}
            value={filters.project_id}
            onChange={(arr) => setF("project_id", arr)}
            placeholder="— Any project —" />
          {(filters.company_id?.length ?? 0) > 0 && (
            <span className="text-xxs text-dim" title="Picking a parent expands to every descendant company">
              ↳ {expandedCompanyIds.size} companies via hierarchy
            </span>
          )}
          {hasFilter && (
            <button className="btn btn-ghost btn-sm" onClick={resetFilters}>Reset</button>
          )}
        </div>
      </div>

      {/* ── Tab strip + layout toggle ── */}
      <div className="flex justify-between items-center mb-16" style={{ flexWrap: "wrap", gap: 10 }}>
        <div className="tabs">
          <button className={`tab ${activeTab === "keys" ? "active" : ""}`}
            onClick={() => setActiveTab("keys")}>Yearly Key Projection</button>
          <button className={`tab ${activeTab === "finance" ? "active" : ""}`}
            onClick={() => setActiveTab("finance")}>Project Finance</button>
          <button className={`tab ${activeTab === "cashflow" ? "active" : ""}`}
            onClick={() => setActiveTab("cashflow")}>Yearly Cashflow</button>
          <button className={`tab ${activeTab === "pnl" ? "active" : ""}`}
            onClick={() => setActiveTab("pnl")}>P&amp;L Statement</button>
        </div>
        <div className="flex items-center" style={{ gap: 6 }}>
          <span className="text-xxs text-muted fw-600" style={{ textTransform: "uppercase", letterSpacing: 0.6 }}>Layout:</span>
          <button className={`btn btn-sm ${layout === "stacked" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setLayout("stacked")} title="Chart above table">
            ▭ Stacked
          </button>
          <button className={`btn btn-sm ${layout === "side-by-side" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setLayout("side-by-side")} title="Chart + table side-by-side">
            ▭▭ Side-by-Side
          </button>
        </div>
      </div>

      {activeTab === "keys" && (
        <KeyProjectionTable data={keyData} loading={loadingKeys} layout={layout} />
      )}
      {activeTab === "finance" && (
        <ProjectFinanceTable data={finData} loading={loadingFin} layout={layout} />
      )}
      {activeTab === "cashflow" && (
        <CashflowTable data={cashData} loading={loadingCash} layout={layout} />
      )}
      {activeTab === "pnl" && (
        <PnlTable data={pnlData} loading={loadingPnl} layout={layout} />
      )}
    </div>
  );
}


/* ─── Generic layout wrapper — chart on top OR side-by-side. ─────── */
function ChartTableLayout({ chart, table, layout }) {
  if (layout === "side-by-side") {
    return (
      <div className="grid-2 gap-16" style={{ alignItems: "start" }}>
        <div>{chart}</div>
        <div>{table}</div>
      </div>
    );
  }
  return (
    <>
      {chart}
      <div style={{ marginTop: 16 }}>{table}</div>
    </>
  );
}


/* ─── Lightweight inline SVG charts — no external dep. ─────────────
   KeysChart: bars for "No. of Keys" per period + line overlay for the
              cumulative total, mirroring the Excel chart in the spec.
   FinanceChart: stacked bars for Promoters + Bank Loan per FY, plus
                 a filled area showing cumulative spending.
*/
function KeysChart({ data }) {
  if (!data || !data.years || !data.years.length) return null;
  const { years, pre_window_total, added_by_year, cumulative_by_year, pipeline_total } = data;

  // Build the x-axis: opening | each FY | Pipeline.
  const openingLabel = `As on Mar '${String(years[0]).slice(-2)}`;
  const xLabels = [openingLabel, ...years.map(fyLabel), "Pipeline"];
  const barValues = [pre_window_total, ...years.map((y) => added_by_year[y] || 0), pipeline_total];
  // Cumulative line includes opening and each year. No line over Pipeline
  // (pipeline keys aren't slotted to any FY yet).
  const lineValues = [pre_window_total, ...years.map((y) => cumulative_by_year[y] || 0), null];

  return (
    <ComboChart
      title="Yearly Key Count"
      xLabels={xLabels}
      bars={[{ label: "No. of Keys", values: barValues, color: "#3b66c4" }]}
      lines={[{ label: "Cumulative", values: lineValues, color: "#f59e0b" }]}
    />
  );
}

function FinanceChart({ data }) {
  if (!data || !data.years || !data.years.length) return null;
  const { years, rows, pre_window, pipeline } = data;
  const xLabels = [
    `As on Year Ending March '${String(years[0]).slice(-2)}`,
    ...years.map(fyLabel),
    "Pipeline",
  ];
  const promoter = [pre_window.promoter, ...rows.map((r) => r.promoter), pipeline.promoter];
  const bank     = [pre_window.bank,     ...rows.map((r) => r.bank),     pipeline.bank];
  const cumulative = [pre_window.total, ...rows.map((r) => r.cumulative_total), null];

  return (
    <ComboChart
      title="Project Financing"
      xLabels={xLabels}
      stacked
      bars={[
        { label: "Promoters Contribution", values: promoter, color: "#22c55e" },
        { label: "Bank Loan / Secured Loan", values: bank, color: "#ea580c" },
      ]}
      lines={[
        { label: "Cumulative Spending", values: cumulative, color: "#94a3b8",
          area: "rgba(148,163,184,0.18)" },
      ]}
    />
  );
}

function ComboChart({ title, xLabels, bars, lines, stacked = false }) {
  // Compact dimensions — vs the original 320-tall chart, this leaves
  // room for the table just below it and on smaller screens, and
  // matches the user's "consume less space" feedback.
  const W = 880;
  const H = 210;
  const PAD = { top: 18, right: 12, bottom: 38, left: 44 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const n = xLabels.length;

  // Compute y-max: stacked bars use the column sum; otherwise the max bar/line.
  const stackedMax = stacked
    ? xLabels.map((_, i) => bars.reduce((s, b) => s + Math.max(0, b.values[i] || 0), 0))
    : null;
  const flatBarMax = Math.max(0, ...bars.flatMap((b) => b.values.map((v) => v || 0)));
  const lineMax = Math.max(0, ...lines.flatMap((l) => l.values.filter((v) => v != null)));
  const rawMax = Math.max(
    flatBarMax,
    lineMax,
    stackedMax ? Math.max(...stackedMax) : 0,
  ) || 1;
  // Round y-max up to a nice number (1, 2, 5 × 10^k).
  const niceMax = niceCeil(rawMax);

  const xStep = innerW / n;
  const barGroupW = xStep * 0.55;

  const xCenter = (i) => PAD.left + xStep * i + xStep / 2;
  const yPx = (v) => PAD.top + innerH - (Math.max(0, v) / niceMax) * innerH;

  // Y-axis ticks (5 evenly spaced).
  const ticks = [0, 1, 2, 3, 4].map((k) => (niceMax / 4) * k);

  // Build line paths.
  const linePath = (vals) => {
    let d = "";
    let started = false;
    vals.forEach((v, i) => {
      if (v == null) { started = false; return; }
      const x = xCenter(i);
      const y = yPx(v);
      d += (started ? " L" : "M") + x + "," + y;
      started = true;
    });
    return d;
  };

  return (
    <div style={{
      background: "rgba(255,255,255,0.02)", border: "1px solid var(--border)",
      borderRadius: 8, padding: 10, marginTop: 8,
    }}>
      {/* Header row: title on the left, legend chips inline on the right.
          Saves vertical space and groups the metadata at the top. */}
      <div className="flex justify-between items-center mb-6" style={{ flexWrap: "wrap", gap: 8 }}>
        <p className="text-xs fw-600" style={{ color: "var(--text-secondary)", margin: 0 }}>{title}</p>
        <div className="flex" style={{ gap: 10, flexWrap: "wrap" }}>
          {bars.map((b, i) => (
            <span key={`b${i}`} className="text-xxs" style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 11, height: 8, background: b.color, display: "inline-block", borderRadius: 2 }} />
              {b.label}
            </span>
          ))}
          {lines.map((l, i) => (
            <span key={`l${i}`} className="text-xxs" style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 14, height: 2, background: l.color, display: "inline-block" }} />
              {l.label}
            </span>
          ))}
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet"
        style={{ width: "100%", height: "auto", display: "block", maxHeight: 240 }}>
        {/* Y-grid + tick labels */}
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={PAD.left} x2={W - PAD.right} y1={yPx(t)} y2={yPx(t)}
              stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
            <text x={PAD.left - 6} y={yPx(t) + 3}
              textAnchor="end" fontSize="9" fill="var(--text-muted)">
              {fmt(t)}
            </text>
          </g>
        ))}
        {/* Bars */}
        {xLabels.map((_, i) => {
          if (stacked) {
            // Stacked: each segment on top of the previous.
            let yCursor = yPx(0);
            return (
              <g key={i}>
                {bars.map((b, bi) => {
                  const v = Math.max(0, b.values[i] || 0);
                  if (!v) return null;
                  const h = (v / niceMax) * innerH;
                  yCursor -= h;
                  return (
                    <rect key={bi} x={xCenter(i) - barGroupW / 2} y={yCursor}
                      width={barGroupW} height={h} fill={b.color} />
                  );
                })}
              </g>
            );
          }
          // Side-by-side bars (single bar in our case → centered).
          const subW = barGroupW / Math.max(1, bars.length);
          return (
            <g key={i}>
              {bars.map((b, bi) => {
                const v = Math.max(0, b.values[i] || 0);
                const h = (v / niceMax) * innerH;
                const x = xCenter(i) - barGroupW / 2 + subW * bi;
                return (
                  <g key={bi}>
                    <rect x={x} y={yPx(v)} width={subW * 0.92} height={h} fill={b.color} />
                    {v > 0 && (
                      <text x={x + (subW * 0.92) / 2} y={Math.max(yPx(v) - 4, 10)}
                        textAnchor="middle" fontSize="9" fill="var(--text-secondary)">{fmt(v)}</text>
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}
        {/* Line / area overlay */}
        {lines.map((l, li) => {
          const d = linePath(l.values);
          if (!d) return null;
          // Area path: same line + close to baseline.
          let areaD = null;
          if (l.area) {
            // build area: path then dropping to baseline.
            let segments = []; let cur = [];
            l.values.forEach((v, i) => {
              if (v == null) {
                if (cur.length) segments.push(cur);
                cur = [];
              } else cur.push(i);
            });
            if (cur.length) segments.push(cur);
            areaD = segments.map((seg) => {
              let s = "M" + xCenter(seg[0]) + "," + yPx(0);
              seg.forEach((i) => { s += " L" + xCenter(i) + "," + yPx(l.values[i]); });
              s += " L" + xCenter(seg[seg.length - 1]) + "," + yPx(0) + " Z";
              return s;
            }).join(" ");
          }
          return (
            <g key={li}>
              {areaD && <path d={areaD} fill={l.area} />}
              <path d={d} fill="none" stroke={l.color} strokeWidth="2.4" />
              {l.values.map((v, i) => {
                if (v == null) return null;
                // Decide if there's a bar at this x position. If yes and the
                // line value is close to the column-top of the bars, push the
                // line label ABOVE the bar label to avoid the same-Y overlap
                // seen with cumulative-style lines.
                const colTop = stacked
                  ? bars.reduce((s, b) => s + Math.max(0, b.values[i] || 0), 0)
                  : Math.max(0, ...bars.map((b) => b.values[i] || 0));
                // 1.5%-of-max tolerance lets equal values count as colliding.
                const tol = niceMax * 0.015;
                const collidesWithBarLabel = colTop > 0
                  && Math.abs(v - colTop) <= Math.max(tol, 0.5);
                // Per-line vertical stagger keeps multiple line series from
                // overlapping each other (li = line index in the lines array).
                const yOffset = collidesWithBarLabel
                  ? 16 + li * 13     // above the bar label, with stagger
                  : 7 + li * 13;     // standard "just above the node"
                const labelY = Math.max(yPx(v) - yOffset, 10);
                return (
                  <g key={i}>
                    <circle cx={xCenter(i)} cy={yPx(v)} r="2.6" fill={l.color} />
                    <text x={xCenter(i)} y={labelY}
                      textAnchor="middle" fontSize="9" fill={l.color} fontWeight="600">{fmt(v)}</text>
                  </g>
                );
              })}
            </g>
          );
        })}
        {/* X-axis labels — angle slightly when many columns to avoid overlap */}
        {xLabels.map((lbl, i) => (
          <text key={i} x={xCenter(i)} y={H - PAD.bottom + 12}
            textAnchor="middle" fontSize="9" fill="var(--text-muted)">{lbl}</text>
        ))}
        {/* Baseline */}
        <line x1={PAD.left} x2={W - PAD.right} y1={yPx(0)} y2={yPx(0)}
          stroke="var(--border)" strokeWidth="1" />
      </svg>
      {/* Legend now lives in the header row above the chart. */}
    </div>
  );
}

// Round up to the nearest "nice" number (1, 2, 5 × 10^k) for axis maxes.
function niceCeil(x) {
  if (!x || x <= 0) return 1;
  const exp = Math.floor(Math.log10(x));
  const base = Math.pow(10, exp);
  const m = x / base;
  let mult;
  if (m <= 1) mult = 1;
  else if (m <= 2) mult = 2;
  else if (m <= 5) mult = 5;
  else mult = 10;
  return mult * base;
}


function KeyProjectionTable({ data, loading, layout }) {
  if (loading) return <div className="loading-container"><div className="spinner" /></div>;
  if (!data || !data.years || data.years.length === 0) {
    return (
      <div className="card" style={{ textAlign: "center", padding: 32 }}>
        <p className="text-muted">No data for the selected filters.</p>
      </div>
    );
  }
  const { years, pre_window_total, added_by_year, cumulative_by_year, pipeline_total, grand_total, project_count } = data;
  const preLabel = years.length ? `As on Mar '${String(years[0]).slice(-2)}` : "Opening";

  const downloadData = {
    title: "Yearly Key Projection",
    columns: [
      { key: "period", label: "Financial Year / Period" },
      { key: "keys", label: "No. of Keys" },
      { key: "cumulative", label: "Cumulative" },
    ],
    rows: [
      { period: preLabel, keys: pre_window_total || 0, cumulative: pre_window_total || 0,
        _formula_text: "Sum of sub-units already Operational before the FY window" },
      ...years.map((y) => ({
        period: fyLabel(y),
        keys: added_by_year[y] || 0,
        cumulative: cumulative_by_year[y] || 0,
        _formula_text: "Sum of sub-units becoming operational in this FY",
      })),
      { period: "Pipeline", keys: pipeline_total || 0, cumulative: "",
        _formula_text: "Sub-units without a slotted FY (pipeline projects)" },
      { period: "Grand Total", keys: grand_total || 0, cumulative: "" },
    ],
  };

  const chart = (
    <div className="card">
      <div className="flex justify-between items-center gap-8 mb-8" style={{ flexWrap: "wrap" }}>
        <div className="flex items-center gap-8">
          <h3 className="heading fw-600" style={{ fontSize: 17, margin: 0 }}>Yearly Key Projection</h3>
          <InfoTip text={`Rooms (keys) becoming operational per fiscal year across the ${project_count}-project portfolio.`} />
        </div>
        <DownloadMenu filename="yearly_key_projection" getData={() => downloadData} />
      </div>
      <KeysChart data={data} />
    </div>
  );

  const table = (
    <div className="card">
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Financial Year / Period</th>
              <th style={{ textAlign: "right" }}>No. of Keys</th>
              <th style={{ textAlign: "right" }}>Cumulative</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{preLabel}</td>
              <td className="mono" style={{ textAlign: "right" }}>{fmt(pre_window_total)}</td>
              <td className="mono" style={{ textAlign: "right" }}>{fmt(pre_window_total)}</td>
            </tr>
            {years.map((y) => (
              <tr key={y}>
                <td>{fyLabel(y)}</td>
                <td className="mono" style={{ textAlign: "right" }}>{fmt(added_by_year[y])}</td>
                <td className="mono" style={{ textAlign: "right" }}>{fmt(cumulative_by_year[y])}</td>
              </tr>
            ))}
            <tr>
              <td>Pipeline</td>
              <td className="mono" style={{ textAlign: "right" }}>{fmt(pipeline_total)}</td>
              <td className="mono" style={{ textAlign: "right" }}>—</td>
            </tr>
            <tr className="fw-600" style={{ borderTop: "2px solid var(--border)" }}>
              <td>Grand Total</td>
              <td className="mono" style={{ textAlign: "right", color: "var(--accent)" }}>{fmt(grand_total)}</td>
              <td>—</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  return <ChartTableLayout layout={layout} chart={chart} table={table} />;
}


function ProjectFinanceTable({ data, loading, layout }) {
  if (loading) return <div className="loading-container"><div className="spinner" /></div>;
  if (!data || !data.years || data.years.length === 0) {
    return (
      <div className="card" style={{ textAlign: "center", padding: 32 }}>
        <p className="text-muted">No data for the selected filters.</p>
      </div>
    );
  }
  const { years, rows, pre_window, pipeline, grand_total, project_count } = data;
  const preLabel = years.length ? `As on Year Ending March '${String(years[0]).slice(-2)}` : "Opening";

  const financeDownload = {
    title: "Project Finance",
    columns: [
      { key: "period", label: "Financial Year / Period" },
      { key: "promoter", label: "Promoters Contribution" },
      { key: "bank", label: "Bank Loan" },
      { key: "cum_total", label: "Cumulative Total" },
      { key: "total", label: "Total Cost (Incurred / Budgeted)" },
      { key: "cum_promoter", label: "Cumulative Promoters" },
      { key: "cum_bank", label: "Cumulative Bank Loan" },
    ],
    rows: [
      { period: preLabel,
        promoter: pre_window.promoter, bank: pre_window.bank,
        cum_total: pre_window.total, total: pre_window.total,
        cum_promoter: pre_window.promoter, cum_bank: pre_window.bank,
        _formula_text: "Capex incurred before the FY window" },
      ...rows.map((r) => ({
        period: fyLabel(r.year),
        promoter: r.promoter, bank: r.bank,
        cum_total: r.cumulative_total, total: r.total,
        cum_promoter: r.cumulative_promoter, cum_bank: r.cumulative_bank,
        _formula_text: "Year-wise capex × project MoF ratio",
      })),
      { period: "Pipeline",
        promoter: pipeline.promoter, bank: pipeline.bank,
        cum_total: "", total: pipeline.total,
        cum_promoter: "", cum_bank: "",
        _formula_text: "Capex on projects flagged Pipeline" },
      { period: "Grand Total",
        promoter: grand_total.promoter, bank: grand_total.bank,
        cum_total: "", total: grand_total.total,
        cum_promoter: "", cum_bank: "" },
    ],
  };

  const chart = (
    <div className="card">
      <div className="flex justify-between items-center gap-8 mb-8" style={{ flexWrap: "wrap" }}>
        <div className="flex items-center gap-8">
          <h3 className="heading fw-600" style={{ fontSize: 17, margin: 0 }}>Project Finance</h3>
          <InfoTip text={`Year-wise capex outflow across the ${project_count}-project portfolio, split into Promoters Contribution and Bank Loan using each project's Means-of-Finance ratio.`} />
        </div>
        <DownloadMenu filename="project_finance" getData={() => financeDownload} />
      </div>
      <FinanceChart data={data} />
    </div>
  );

  const table = (
    <div className="card">
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Financial Year / Period</th>
              {/* Multi-line column headers — keep table compact so the
                  user doesn't need to scroll horizontally. */}
              <th style={{ textAlign: "right", whiteSpace: "normal", lineHeight: 1.2 }}>Promoters<br />Contribution</th>
              <th style={{ textAlign: "right", whiteSpace: "normal", lineHeight: 1.2 }}>Bank Loan</th>
              <th style={{ textAlign: "right", whiteSpace: "normal", lineHeight: 1.2 }}>Cumulative<br />Total</th>
              <th style={{ textAlign: "right", whiteSpace: "normal", lineHeight: 1.2 }}>Total Cost<br />(Incurred / Budgeted)</th>
              <th style={{ textAlign: "right", whiteSpace: "normal", lineHeight: 1.2 }}>Cumulative<br />Promoters</th>
              <th style={{ textAlign: "right", whiteSpace: "normal", lineHeight: 1.2 }}>Cumulative<br />Bank Loan</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{preLabel}</td>
              <td className="mono" style={{ textAlign: "right" }}>{fmt(pre_window.promoter)}</td>
              <td className="mono" style={{ textAlign: "right" }}>{fmt(pre_window.bank)}</td>
              <td className="mono" style={{ textAlign: "right" }}>{fmt(pre_window.total)}</td>
              <td className="mono" style={{ textAlign: "right" }}>{fmt(pre_window.total)}</td>
              <td className="mono" style={{ textAlign: "right" }}>{fmt(pre_window.promoter)}</td>
              <td className="mono" style={{ textAlign: "right" }}>{fmt(pre_window.bank)}</td>
            </tr>
            {rows.map((r) => (
              <tr key={r.year}>
                <td>{fyLabel(r.year)}</td>
                <td className="mono" style={{ textAlign: "right" }}>{fmt(r.promoter)}</td>
                <td className="mono" style={{ textAlign: "right" }}>{fmt(r.bank)}</td>
                <td className="mono" style={{ textAlign: "right" }}>{fmt(r.cumulative_total)}</td>
                <td className="mono" style={{ textAlign: "right" }}>{fmt(r.total)}</td>
                <td className="mono" style={{ textAlign: "right" }}>{fmt(r.cumulative_promoter)}</td>
                <td className="mono" style={{ textAlign: "right" }}>{fmt(r.cumulative_bank)}</td>
              </tr>
            ))}
            <tr>
              <td>Pipeline</td>
              <td className="mono" style={{ textAlign: "right" }}>{fmt(pipeline.promoter)}</td>
              <td className="mono" style={{ textAlign: "right" }}>{fmt(pipeline.bank)}</td>
              <td className="mono" style={{ textAlign: "right" }}>—</td>
              <td className="mono" style={{ textAlign: "right" }}>{fmt(pipeline.total)}</td>
              <td className="mono" style={{ textAlign: "right" }}>—</td>
              <td className="mono" style={{ textAlign: "right" }}>—</td>
            </tr>
            <tr className="fw-600" style={{ borderTop: "2px solid var(--border)" }}>
              <td>Grand Total</td>
              <td className="mono" style={{ textAlign: "right", color: "var(--accent)" }}>{fmt(grand_total.promoter)}</td>
              <td className="mono" style={{ textAlign: "right", color: "var(--accent)" }}>{fmt(grand_total.bank)}</td>
              <td>—</td>
              <td className="mono" style={{ textAlign: "right", color: "var(--accent)" }}>{fmt(grand_total.total)}</td>
              <td>—</td>
              <td>—</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-xxs text-dim mt-12">
        * Promoter / Bank split is derived from each project's Means-of-Finance ratio. Set Means of Finance per project under Input → Capex Means of Finance for an accurate split.
      </p>
    </div>
  );

  return <ChartTableLayout layout={layout} chart={chart} table={table} />;
}


/* ─── Cashflow tab ──────────────────────────────────────────────── */
function CashflowTable({ data, loading, layout }) {
  if (loading) return <div className="loading-container"><div className="spinner" /></div>;
  if (!data || !data.years || data.years.length === 0) {
    return (
      <div className="card" style={{ textAlign: "center", padding: 32 }}>
        <p className="text-muted">No data for the selected filters.</p>
      </div>
    );
  }
  const { years, rows, project_count } = data;

  // The rows array is structured by the backend with named "categories"
  // (operational / financing / closing) and section subtotal flags.
  const renderRow = (r, idx) => {
    const isSubtotal = r.subtotal;
    const isBold = isSubtotal || r.bold;
    return (
      <tr key={r.label + idx}
        style={{
          background: isSubtotal ? "rgba(124,143,245,0.10)" : undefined,
          fontWeight: isBold ? 600 : 400,
          borderTop: r.separator_above ? "2px solid var(--border)" : undefined,
        }}>
        <td style={{ color: isSubtotal ? "var(--accent)" : "var(--text-primary)" }}>{r.label}</td>
        {years.map((y) => {
          const v = (r.values || {})[String(y)];
          const display = v == null ? "—" : (Math.abs(v) < 0.5 ? "—" : fmtCr(v));
          return (
            <td key={y} className="mono" style={{
              textAlign: "right",
              color: v != null && v < 0 ? "var(--red-light)" :
                     isSubtotal ? "var(--accent)" : "var(--text-secondary)",
            }}>{display}</td>
          );
        })}
      </tr>
    );
  };

  const yearTag = (y, i) => i === 0 ? "Actual" : i === 1 ? "Budgeted" : "Projected";

  const cashflowDownload = {
    title: "Yearly Cashflow",
    columns: [
      { key: "label", label: "Particulars" },
      ...years.map((y, i) => ({
        key: `y${y}`,
        label: `FY ${y} (${i === 0 ? "Actual" : i === 1 ? "Budgeted" : "Projected"})`,
      })),
    ],
    rows: rows.map((r) => ({
      label: r.label,
      ...Object.fromEntries(years.map((y) => [`y${y}`,
        (r.values || {})[String(y)] == null ? "" : (r.values || {})[String(y)]])),
      _formula_text: r.subtotal ? "Subtotal row" : "",
    })),
  };

  const chart = (
    <div className="card">
      <div className="flex justify-between items-center gap-8 mb-8" style={{ flexWrap: "wrap" }}>
        <div className="flex items-center gap-8">
          <h3 className="heading fw-600" style={{ fontSize: 17, margin: 0 }}>Yearly Cashflow</h3>
          <span className="text-xxs text-dim">(₹ Cr)</span>
          <InfoTip text={`Operational + Investing + Financing cashflows across the ${project_count}-project portfolio. Values in ₹ Cr.`} />
        </div>
        <DownloadMenu filename="yearly_cashflow" getData={() => cashflowDownload} />
      </div>
      <CashflowChart data={data} />
    </div>
  );

  const table = (
    <div className="card">
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Particulars</th>
              {years.map((y, i) => (
                <th key={y} style={{ textAlign: "right" }}>
                  FY {y}<br />
                  <span className="text-xxs text-dim" style={{ fontWeight: 400 }}>{yearTag(y, i)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(renderRow)}
          </tbody>
        </table>
      </div>
    </div>
  );

  return <ChartTableLayout layout={layout} chart={chart} table={table} />;
}

function fmtCr(n) {
  if (n == null || isNaN(n)) return "—";
  // Match the Excel feel: integer for ₹ Cr, comma separator (Indian).
  return Math.round(Number(n)).toLocaleString("en-IN");
}

function CashflowChart({ data }) {
  if (!data || !data.years || !data.years.length) return null;
  const { years, rows } = data;
  // Build series from named rows. Look up by label.
  const findRow = (label) => rows.find((r) => r.label === label);
  const opRow = findRow("A. Cashflow from Operational Activities");
  const invRow = findRow("B. Cashflow from Investing Activities");
  const finRow = findRow("C. Cashflow from Financing Activities");
  const netRow = findRow("Net Cashflow (A+B+C)");
  if (!opRow || !invRow || !finRow) return null;
  const seriesVals = (r) => years.map((y) => (r.values || {})[String(y)] || 0);
  return (
    <ComboChart
      title="Yearly Cashflow"
      xLabels={years.map(fyLabel)}
      bars={[
        { label: "Operational",  values: seriesVals(opRow),  color: "#22c55e" },
        { label: "Financing",    values: seriesVals(finRow), color: "#3b66c4" },
        { label: "Investing",    values: seriesVals(invRow), color: "#ea580c" },
      ]}
      lines={netRow ? [{
        label: "Net Cashflow",
        values: seriesVals(netRow),
        color: "#f59e0b",
      }] : []}
    />
  );
}


/* ─── P&L Statement tab ──────────────────────────────────────── */
function PnlTable({ data, loading, layout }) {
  if (loading) return <div className="loading-container"><div className="spinner" /></div>;
  if (!data || !data.years || data.years.length === 0) {
    return (
      <div className="card" style={{ textAlign: "center", padding: 32 }}>
        <p className="text-muted">No data for the selected filters.</p>
      </div>
    );
  }
  const { years, rows, project_count } = data;
  const yearTag = (y, i) => i === 0 ? "Actual" : i === 1 ? "Budgeted" : "Projected";

  const renderRow = (r, idx) => {
    if (r.header) {
      return (
        <tr key={r.label + idx} style={{ background: "rgba(255,255,255,0.04)" }}>
          <td colSpan={years.length + 1} className="fw-700"
            style={{ color: "var(--accent)", padding: "8px 12px", letterSpacing: 0.4 }}>{r.label}</td>
        </tr>
      );
    }
    const isSubtotal = r.subtotal;
    return (
      <tr key={r.label + idx}
        style={{
          background: isSubtotal ? "rgba(124,143,245,0.10)" : undefined,
          fontWeight: isSubtotal ? 600 : 400,
          borderTop: r.separator_above ? "2px solid var(--border)" : undefined,
        }}>
        <td style={{ color: isSubtotal ? "var(--accent)" : "var(--text-primary)" }}>{r.label}</td>
        {years.map((y) => {
          const v = (r.values || {})[String(y)];
          const display = v == null ? "—" : (Math.abs(v) < 0.5 ? "—" : fmtCr(v));
          return (
            <td key={y} className="mono" style={{
              textAlign: "right",
              color: v != null && v < 0 ? "var(--red-light)" :
                     isSubtotal ? "var(--accent)" : "var(--text-secondary)",
            }}>{display}</td>
          );
        })}
      </tr>
    );
  };

  // Build chart series from named subtotal rows.
  const findRow = (label) => rows.find((r) => r.label === label);
  const totalRev = findRow("Total Revenue");
  const ebitda   = findRow("EBITDA");
  const pat      = findRow("Profit After Tax");
  const seriesVals = (r) => r ? years.map((y) => (r.values || {})[String(y)] || 0) : [];

  const pnlDownload = {
    title: "Profit & Loss Statement",
    columns: [
      { key: "label", label: "Particulars" },
      ...years.map((y, i) => ({
        key: `y${y}`,
        label: `FY ${y} (${yearTag(y, i)})`,
      })),
    ],
    rows: rows
      .filter((r) => !r.header)   // skip the visual "Hospitality" section header
      .map((r) => ({
        label: r.label,
        ...Object.fromEntries(years.map((y) => [`y${y}`,
          (r.values || {})[String(y)] == null ? "" : (r.values || {})[String(y)]])),
        _formula_text: r.subtotal ? "Subtotal row" : "",
      })),
  };

  const chart = (
    <div className="card">
      <div className="flex justify-between items-center gap-8 mb-8" style={{ flexWrap: "wrap" }}>
        <div className="flex items-center gap-8">
          <h3 className="heading fw-600" style={{ fontSize: 17, margin: 0 }}>Profit &amp; Loss Statement</h3>
          <span className="text-xxs text-dim">(₹ Cr)</span>
          <InfoTip text={`Revenue, EBITDA, and PAT aggregated across the ${project_count}-project portfolio. Values in ₹ Cr.`} />
        </div>
        <DownloadMenu filename="pnl_statement" getData={() => pnlDownload} />
      </div>
      <ComboChart
        title="P&L — Total Revenue, EBITDA, PAT"
        xLabels={years.map((y, i) => `FY ${y} · ${yearTag(y, i)}`)}
        bars={[
          { label: "Total Revenue", values: seriesVals(totalRev), color: "#3b66c4" },
        ]}
        lines={[
          { label: "EBITDA", values: seriesVals(ebitda), color: "#22c55e" },
          { label: "PAT",    values: seriesVals(pat),    color: "#f59e0b" },
        ]}
      />
    </div>
  );

  const table = (
    <div className="card">
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Particulars</th>
              {years.map((y, i) => (
                <th key={y} style={{ textAlign: "right" }}>
                  FY {y}<br />
                  <span className="text-xxs text-dim" style={{ fontWeight: 400 }}>{yearTag(y, i)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{rows.map(renderRow)}</tbody>
        </table>
      </div>
      <p className="text-xxs text-dim mt-12">
        * Lease &amp; Rental / Real Estate / RHPL equity rows are placeholders — they're not yet derived from any data source.
      </p>
    </div>
  );

  return <ChartTableLayout layout={layout} chart={chart} table={table} />;
}
